import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Package, Truck, Award, FileCheck, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PartnerStorefrontSetup from "./PartnerStorefrontSetup";
import PartnerCompletionChecklist from "./PartnerCompletionChecklist";

const NEXT = { received: "preparing", preparing: "ready_for_driver", ready_for_driver: null };
const NEXT_LABEL = { received: "Start preparing", preparing: "Ready for driver" };

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get("/partner/me"); setData(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, []);

  const transition = async (id, status) => {
    setBusy(true); setErr("");
    try { await api.post(`/partner/me/orders/${id}/transition`, { status }); await reload(); }
    catch (e) { setErr(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  if (err) return <div className="text-rose-300">{err}</div>;
  if (!data) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING PARTNER DECK…</div>;

  const compliance = data.profile.compliance.status;
  const isApproved = compliance === "approved";

  return (
    <div data-testid="partner-dashboard" className="space-y-10">
      <header className="fade-up">
        <div className="eyebrow flex items-center gap-2"><Store className="h-3 w-3" /> Partner console</div>
        <h1 className="headline-xl">{data.profile.business_name}</h1>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <span className={`chip ${isApproved ? "chip--green" : ""}`}>Compliance: {compliance}</span>
          {data.partner_certified && <span className="chip chip--purple"><Award className="h-3 w-3" /> Lifted Partner Certified</span>}
        </div>
        {!isApproved && (
          <div className="mt-4 glass-panel p-4 border-rose-500/30 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-300" />
            <div className="text-sm">
              <div className="text-lifted-text">Your partner profile is <strong>{compliance}</strong>.</div>
              <div className="text-lifted-dim">Upload required documents and ask an admin to approve before you can receive orders.</div>
            </div>
          </div>
        )}
      </header>

      {/* Fulfillment queue */}
      <section className="glass-panel p-6">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><Package className="h-3 w-3" /> Fulfillment queue</div>
        <table className="w-full mt-4 text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="py-2">When</th><th>Customer order</th><th>Drop-off</th><th>Status</th><th className="text-right">Next</th>
            </tr>
          </thead>
          <tbody>
            {data.orders.map((o) => (
              <tr key={o.id} data-testid={`partner-order-${o.id}`} className="border-b border-white/5">
                <td className="py-2 text-xs text-lifted-dim">{o.created_at?.slice(0,16).replace("T"," ")}</td>
                <td className="text-xs text-lifted-text">{o.customer_order_id}</td>
                <td className="text-xs text-lifted-dim">{o.dropoff_address}</td>
                <td>{{
                  received: <span className="chip">Received</span>,
                  preparing: <span className="chip chip--purple">Preparing</span>,
                  ready_for_driver: <span className="chip chip--green">Ready · driver</span>,
                  handed_off: <span className="chip chip--green">With driver</span>,
                  completed: <span className="chip chip--green">Completed</span>,
                  cancelled: <span className="chip">Cancelled</span>,
                  failed: <span className="chip">Failed</span>,
                }[o.status]}</td>
                <td className="text-right">
                  {NEXT[o.status] && isApproved && (
                    <button data-testid={`partner-transition-${o.id}-${NEXT[o.status]}`} onClick={() => transition(o.id, NEXT[o.status])} disabled={busy} className="btn-lifted !py-1.5 !px-3 !text-[0.65rem]">
                      <Truck className="h-3 w-3" /> {NEXT_LABEL[o.status]}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data.orders.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-lifted-mute">No orders yet.</td></tr>}
          </tbody>
        </table>
      </section>

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Completed" value={data.profile.stats.orders_completed} />
        <Stat label="Cancelled" value={data.profile.stats.orders_cancelled} />
        <Stat label="Avg fulfillment" value={fmtSec(data.profile.stats.avg_fulfillment_seconds)} />
        <Stat label="Avg handoff" value={fmtSec(data.profile.stats.avg_handoff_seconds)} />
      </section>

      <section className="glass-panel p-6">
        <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><FileCheck className="h-3 w-3" /> Compliance documents</div>
        <ul className="mt-3 text-sm space-y-2">
          {data.documents.map((d) => (
            <li key={d.id} className="flex items-center justify-between border-b border-white/5 pb-2 last:border-b-0">
              <div>
                <div className="text-lifted-text">{d.label} · <span className="text-lifted-mute">{d.kind}</span></div>
                <div className="text-xs text-lifted-dim">{d.url}</div>
              </div>
              {{ pending: <span className="chip">Pending</span>,
                 approved: <span className="chip chip--green">Approved</span>,
                 rejected: <span className="chip">Rejected</span> }[d.status]}
            </li>
          ))}
          {data.documents.length === 0 && <li className="text-lifted-mute text-xs">No documents uploaded yet.</li>}
        </ul>
      </section>

      <Link to="/training/partner" className="btn-ghost inline-flex">→ Lifted Partner certification</Link>

      {/* Section 2 — Partner Store Completion Helper (read-only checklist) */}
      <PartnerCompletionChecklist
        profile={data.profile}
        partnerSubmissions={data.product_submissions || data.products || []}
      />

      {/* Section 2 — Public storefront setup (logo, address, map, bio) */}
      <PartnerStorefrontSetup
        profile={data.profile}
        onSaved={(updated) => setData((d) => d ? { ...d, profile: { ...d.profile, ...updated } } : d)}
      />
    </div>
  );
}

function Stat({ label, value }) { return (
  <div className="glass-panel p-4">
    <div className="eyebrow !text-lifted-text-dim">{label}</div>
    <div className="mt-1 font-anton text-2xl text-lifted-text">{value}</div>
  </div>);
}
function fmtSec(s) { if (s == null) return "—"; return s < 60 ? `${s}s` : `${Math.round(s/60)}m`; }
